import { KeyboardEvent, useState } from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface FormFieldTagInputProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  control: Control<T>;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  /** Returns an error message for an invalid value, or null when it can be added. */
  validate?: (value: string) => string | null;
}

/** Free-text list field: each entry is added as a removable chip. Holds a string[] in the form. */
const FormFieldTagInput = <T extends FieldValues>({
  name,
  label,
  control,
  placeholder,
  required,
  maxLength,
  validate,
}: FormFieldTagInputProps<T>) => {
  const { field, fieldState } = useController({ name, control });
  const tags: string[] = Array.isArray(field.value) ? field.value : [];
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  // A pasted list ("Ocean, Monmouth") becomes one chip per entry; invalid entries stay in the input.
  const addDraft = () => {
    const values = draft
      .split(',')
      .map((value) => value.trim().replace(/\s+/g, ' '))
      .filter(Boolean);
    const next = [...tags];
    const rejected: string[] = [];
    let message: string | null = null;
    for (const value of values) {
      const valueError = validate?.(value) ?? null;
      if (valueError) {
        rejected.push(value);
        message ??= valueError;
      } else if (!next.some((tag) => tag.toLowerCase() === value.toLowerCase())) {
        next.push(value);
      }
    }
    if (next.length !== tags.length) field.onChange(next);
    setDraft(rejected.join(', '));
    setError(message);
  };

  const removeTag = (index: number) => {
    field.onChange(tags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addDraft();
    } else if (e.key === 'Backspace' && !draft && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  const errorMessage = error ?? fieldState.error?.message;

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={name} className="flex items-center gap-1 text-sm font-medium">
        {label}
        {required && <span className="text-error align-top">*</span>}
      </Label>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 py-1" data-testid={`${name}-tags`}>
          {tags.map((tag, index) => (
            <Badge key={tag} variant="secondary" className="w-auto gap-1 pr-1">
              <span>{tag}</span>
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                className="rounded-full p-0.5 hover:bg-muted-foreground/20"
                onClick={() => removeTag(index)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Input
        id={name}
        data-testid={name}
        value={draft}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(e) => {
          setDraft(e.target.value.replace(/^\s+/, ''));
          if (error) setError(null);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          addDraft();
          field.onBlur();
        }}
      />
      {errorMessage && <p className="text-error text-xs">{errorMessage}</p>}
    </div>
  );
};

export default FormFieldTagInput;
